import { useOverlay } from '@aigc/overlay-hook';
import { NotificationFilled } from '@ant-design/icons';
import { useNotifications, type Notification } from '@novu/react';
import { Alert, Button, Empty, Popover, Spin } from 'antd';
import { useEffect, useMemo, useRef, useState, type UIEvent } from 'react';
import { getAnnouncementPlainText } from './announcement-content';
import { AnnouncementDetailModal } from './announcement-detail-modal';

const LOAD_MORE_THRESHOLD = 48;

function AnnouncementListItem(props: {
  data: Notification;
  onClick: () => void;
}) {
  const content = useMemo(
    () => getAnnouncementPlainText(props.data.body),
    [props.data.body],
  );

  return (
    <button
      type="button"
      className="block w-full border-0 border-b border-solid border-primary-100 bg-transparent px-4 py-3 text-left last:border-b-0 hover:bg-primary-50"
      onClick={props.onClick}
    >
      <div className="truncate font-medium">{props.data.subject}</div>
      <div className="mt-1 line-clamp-2 text-sm text-primary-500">
        {content}
      </div>
    </button>
  );
}

export function AnnouncementListButton() {
  const [open, setOpen] = useState(false);
  const loading_more_ref = useRef(false);
  const [openOverlay] = useOverlay();
  const {
    notifications,
    error,
    isLoading,
    isFetching,
    hasMore,
    fetchMore,
    refetch,
  } = useNotifications({ archived: false, limit: 10 });

  useEffect(() => {
    if (!isFetching) {
      loading_more_ref.current = false;
    }
  }, [isFetching]);

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    const is_near_bottom =
      element.scrollHeight - element.scrollTop - element.clientHeight <=
      LOAD_MORE_THRESHOLD;

    if (!is_near_bottom || !hasMore || isFetching || loading_more_ref.current) {
      return;
    }

    loading_more_ref.current = true;
    void fetchMore()
      .catch(() => undefined)
      .finally(() => {
        loading_more_ref.current = false;
      });
  };

  const content = (() => {
    if (isLoading) {
      return (
        <div className="flex h-32 items-center justify-center">
          <Spin />
        </div>
      );
    }

    if (error && !notifications?.length) {
      return (
        <div className="p-4">
          <Alert
            type="error"
            title="公告加载失败"
            action={
              <Button size="small" onClick={() => void refetch()}>
                重试
              </Button>
            }
          />
        </div>
      );
    }

    if (!notifications?.length) {
      return <Empty className="py-8" description="暂无公告" />;
    }

    return (
      <div className="max-h-96 overflow-y-auto" onScroll={handleScroll}>
        {notifications.map((notification) => (
          <AnnouncementListItem
            key={notification.id}
            data={notification}
            onClick={() => {
              setOpen(false);
              openOverlay(<AnnouncementDetailModal data={notification} />);
            }}
          />
        ))}
        <div className="py-2 text-center text-xs text-primary-400">
          {isFetching
            ? '加载中…'
            : hasMore
              ? '向下滚动加载更多'
              : '没有更多公告'}
        </div>
      </div>
    );
  })();

  return (
    <Popover
      title="公告列表"
      classNames={{ content: 'w-96 max-w-[80vw]' }}
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
    >
      <Button
        variant="filled"
        color="default"
        icon={<NotificationFilled></NotificationFilled>}
      >
        公告
      </Button>
    </Popover>
  );
}
